/**
 * ChallengeList - List of running challenges
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { cn } from '@/utils/cn';
import type { RunChallenge } from '@/types/running';

export function ChallengeList() {
  const challenges = useRunningStore((state) => state.challenges);
  const fetchChallenges = useRunningStore((state) => state.fetchChallenges);
  const joinChallenge = useRunningStore((state) => state.joinChallenge);

  const [activeTab, setActiveTab] = useState<'active' | 'upcoming' | 'completed'>('active');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadChallenges = async () => {
      setIsLoading(true);
      await fetchChallenges();
      setIsLoading(false);
    };
    loadChallenges();
  }, [fetchChallenges]);

  const handleJoinChallenge = async (challengeId: string) => {
    await joinChallenge(challengeId);
  };

  const now = new Date();
  const activeChallenges = challenges.filter(
    (c) => new Date(c.start_date) <= now && new Date(c.end_date) >= now
  );
  const upcomingChallenges = challenges.filter(
    (c) => new Date(c.start_date) > now
  );

  const displayChallenges = activeTab === 'upcoming' ? upcomingChallenges : activeChallenges;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="px-4 flex gap-2">
        <button
          onClick={() => setActiveTab('active')}
          className={cn(
            'px-4 py-2 rounded-full text-sm font-medium transition-all',
            activeTab === 'active'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-slate-800/50 text-text-secondary'
          )}
        >
          🔥 Active ({activeChallenges.length})
        </button>
        <button
          onClick={() => setActiveTab('upcoming')}
          className={cn(
            'px-4 py-2 rounded-full text-sm font-medium transition-all',
            activeTab === 'upcoming'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-slate-800/50 text-text-secondary'
          )}
        >
          📅 Upcoming ({upcomingChallenges.length})
        </button>
      </div>

      {/* Empty State */}
      {displayChallenges.length === 0 && (
        <div className="text-center py-12 px-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-6xl mb-4"
          >
            🏆
          </motion.div>
          <h3 className="text-xl font-bold text-white mb-2">
            {activeTab === 'upcoming' ? 'No upcoming challenges' : 'No active challenges'}
          </h3>
          <p className="text-text-secondary">
            Check back soon for new challenges!
          </p>
        </div>
      )}

      {/* Challenge List */}
      <div className="px-4 space-y-4">
        {displayChallenges.map((challenge, index) => (
          <ChallengeCard
            key={challenge.id}
            challenge={challenge}
            index={index}
            onJoin={() => handleJoinChallenge(challenge.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface ChallengeCardProps {
  challenge: RunChallenge;
  index: number;
  onJoin: () => void;
}

function ChallengeCard({ challenge, index, onJoin }: ChallengeCardProps) {
  const now = new Date();
  const endDate = new Date(challenge.end_date);
  const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const progress = challenge.user_progress
    ? Math.min(100, (challenge.user_progress / challenge.goal_value) * 100)
    : 0;

  const getChallengeEmoji = (type: string): string => {
    const emojis: Record<string, string> = {
      distance: '📏',
      frequency: '🔢',
      elevation: '⛰️',
      time: '⏱️',
      clean_air: '☁️',
      streak: '🔥',
      segment: '🏁',
    };
    return emojis[type] || '🏆';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl overflow-hidden"
    >
      {/* Image */}
      {challenge.image_url ? (
        <div
          className="h-32 bg-cover bg-center"
          style={{ backgroundImage: `url(${challenge.image_url})` }}
        />
      ) : (
        <div className="h-32 bg-gradient-to-r from-orange-500/20 to-pink-500/20 flex items-center justify-center">
          <span className="text-5xl">{getChallengeEmoji(challenge.challenge_type)}</span>
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-white font-semibold">{challenge.name}</h3>
            <p className="text-text-secondary text-xs">
              {challenge.participants_count} participants
            </p>
          </div>
          <div className="text-right">
            <div className={cn(
              'text-sm font-bold',
              daysLeft <= 3 ? 'text-red-400' : 'text-emerald-400'
            )}>
              {daysLeft} days left
            </div>
          </div>
        </div>

        {/* Goal */}
        <div className="mb-3 p-3 bg-slate-800/50 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-text-secondary text-sm">Goal</span>
            <span className="text-white font-bold">
              {challenge.goal_value} {challenge.goal_unit}
            </span>
          </div>

          {/* Progress Bar */}
          {challenge.is_participating && (
            <div className="relative h-2 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
              />
            </div>
          )}

          {challenge.is_participating && (
            <div className="flex items-center justify-between mt-2 text-xs">
              <span className="text-text-secondary">
                {challenge.user_progress?.toFixed(1)} {challenge.goal_unit}
              </span>
              <span className="text-emerald-400">
                {progress.toFixed(0)}%
              </span>
            </div>
          )}
        </div>

        {/* AQI Constraint - Blurp.ie unique */}
        {challenge.aqi_constraint && (
          <div className="mb-3 p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-xs">
              <span>☁️</span>
              <span className="text-cyan-400">
                Clean Air Challenge: Only runs with AQI &lt; {challenge.aqi_constraint} count!
              </span>
            </div>
          </div>
        )}

        {/* Badge Reward */}
        {challenge.badge_reward && (
          <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-xl">{challenge.badge_reward.icon}</span>
              <div>
                <div className="text-xs text-yellow-400">Reward Badge</div>
                <div className="text-sm text-white">{challenge.badge_reward.name}</div>
              </div>
            </div>
          </div>
        )}

        {/* Action */}
        {!challenge.is_participating ? (
          <button
            onClick={onJoin}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium"
          >
            Join Challenge
          </button>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-emerald-400 text-sm font-medium">
              ✓ Participating
            </span>
            {challenge.user_rank && (
              <span className="text-text-secondary text-sm">
                Rank: #{challenge.user_rank}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default ChallengeList;
